import { useMemo, useState } from 'react';
import type { RouteComponentProps } from 'react-router';
import type { Column, SortingRule } from 'react-table';

import { Action, ResourceType } from '../../../../../typings/permissions';
import { useDialogContext } from '../../../../contexts/DialogContext';
import useDeleteRole from '../../../../hooks/api/roles/useDeleteRole';
import useRoles from '../../../../hooks/api/roles/useRoles';
import useHasPermission from '../../../../shared/permissions/useHasPermission';
import { icons } from '../../../generic';
import Loader from '../../../generic/Loader';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import { InvalidPermissionError } from '../../Errors';
import SettingsTableRowActions from '../../shared/SettingsTableRowActions';
import type { Role } from '../../../../../typings/api';

type Props = RouteComponentProps<object, object>;

const OrganizationRolesIndex = ({ router }: Props) => {
  const hasPermission = useHasPermission();
  const { onConfirm } = useDialogContext();
  const { data: roles = [], isLoading } = useRoles();
  const { mutateAsync: deleteRole } = useDeleteRole();

  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'name', desc: false });
  const [searchValue, setSearchValue] = useState('');

  const canUserManageRoles = hasPermission({
    resourceType: ResourceType.Role,
    action: Action.Manage,
  });

  const filteredRoles = useMemo(() => {
    if (!searchValue) {
      return roles;
    }
    const searchLower = searchValue.toLowerCase();
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(searchLower) ||
        role.description.toLowerCase().includes(searchLower),
    );
  }, [roles, searchValue]);

  const onCloneRoleClick = (role: Role) => {
    router.push(`/account/settings/organization/roles/clone/${role.id}`);
  };

  const onRemoveRoleClick = (role: Role) => {
    onConfirm({
      title: 'Remove Role?',
      body: `Are you sure you want to remove the "${role.name}" role?`,
      onConfirm: () => deleteRole(role.id),
      confirmText: 'Remove',
      confirmBtnType: 'error',
    });
  };

  const getRemoveRoleTooltip = (isCustom: boolean) => {
    if (!isCustom) {
      return 'You are only able to remove custom roles';
    }
    return 'Delete Role';
  };

  const getColumns = (): Array<Column<Role>> => [
    {
      id: 'name',
      Header: <TableHeader title="Role" sort={{ id: 'name', currentMode: sortMode }} />,
      accessor: 'name',
      width: 250,
      resizable: false,
      style: {
        textOverflow: 'ellipsis',
      },
    },
    {
      id: 'description',
      Header: <TableHeader title="Description" sort={{ id: 'description', currentMode: sortMode }} />,
      accessor: 'description',
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ value }) => (
        <span className="role-settings__table__cell__text" title={value}>
          {value}
        </span>
      ),
      resizable: false,
      style: { textOverflow: 'ellipsis', paddingRight: '15px' },
    },
    {
      id: 'actions',
      Header: <TableHeader title="Actions" />,
      accessor: () => null,
      // eslint-disable-next-line react/no-unstable-nested-components
      Cell: ({ original: role }) => (
        <SettingsTableRowActions>
          <SettingsTableRowActions.ViewOrEditLink
            to={`/account/settings/organization/roles/${role.id}`}
            tooltip={role.isCustom ? 'Edit Role' : 'View Role'}
            tooltipId={`edit-role-${role.id}`}
            canEdit={role.isCustom}
          />
          <SettingsTableRowActions.IconButton
            icon={icons.cloneO}
            onClick={() => onCloneRoleClick(role)}
            tooltipId={`clone-role-${role.id}`}
            tooltip="Duplicate Role"
          />
          <SettingsTableRowActions.IconButton
            icon={icons.trashRegular}
            onClick={() => onRemoveRoleClick(role)}
            tooltipId={`delete-role-${role.id}`}
            tooltip={getRemoveRoleTooltip(role.isCustom)}
            isDisabled={!role.isCustom}
          />
        </SettingsTableRowActions>
      ),
      width: 100,
      resizable: false,
      sortable: false,
    },
  ];

  if (!canUserManageRoles) {
    return <InvalidPermissionError />;
  }

  if (isLoading) {
    return (
      <div className="role-settings__loader">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Header heading="Roles" />
      <SettingsPageTableActions>
        <SettingsPageTableActions.NavigationButton
          size="large"
          type="secondary"
          to="/account/settings/organization/roles/add"
          openInNewTab={false}
        >
          Add Role
        </SettingsPageTableActions.NavigationButton>
        <SettingsPageTableActions.SearchInput
          label="Search roles"
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Search roles"
          size="medium"
        />
      </SettingsPageTableActions>
      <SimpleReactTable
        isSortable
        data={filteredRoles}
        columns={getColumns()}
        defaultSortMode={[
          {
            id: 'name',
            desc: false,
          },
        ]}
        onSortedChanged={setSortMode}
        showPagination={filteredRoles.length > 20}
        cacheId="organizationRoles"
      />
    </>
  );
};

export default OrganizationRolesIndex;
